import { FormField } from '@/components/ui/form'
import { useContext, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormEntityContext } from '../../config/context/forms'
import Select, { SelectProps } from '../Select'
import { capitalize } from 'lodash'
import FieldWrapper from './FieldWrapper'
import { MultiSelect } from '@/components/ui/multi-select'
import { cn } from '@/lib/utils'
import { FieldProps } from './types'

const SelectField = <T,>({
  name,
  className,
  disableSorting = false,
  inputClassName,
  options = [],
  isMulti,
  customLabel,
  customDescription,
  customPlaceholder,
  onChange,
  optionLabelKey,
  optionLabelFn,
  optionValueKey,
  transformOptions = true,
  layout = 'inline',
  ...props
}: FieldProps<T> & Partial<Omit<SelectProps, 'options' | 'value'>>) => {
  const { i18n, t } = useTranslation()
  const form = useFormContext()
  const { entityType, i18nPrefix } = useContext(FormEntityContext)

  const description =
    customDescription ??
    (i18n.exists(`${i18nPrefix}.${entityType}.${name}.description`)
      ? t(`${i18nPrefix}.${entityType}.${name}.description`)
      : undefined)

  const transformedOptions = useMemo(() => {
    return transformOptions
      ? options.map((option) => ({
          label: optionLabelFn
            ? optionLabelFn(option)
            : optionLabelKey
            ? String(option[optionLabelKey])
            : capitalize(String(option).replace('_', ' ').replace('_', ' ')),
          value: optionValueKey
            ? (option[optionValueKey] as string)
            : String(option),
          sortLabel: disableSorting ? '' : undefined,
        }))
      : options
  }, [options])

  const currentValue = form.watch(name)

  return (
    <div className={cn('w-full', className)}>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          return (
            <FieldWrapper
              layout={layout}
              label={
                customLabel ?? t(`${i18nPrefix}.${entityType}.${name}.label`)
              }
              description={description}
            >
              {isMulti ? (
                <MultiSelect<string>
                  options={
                    transformedOptions as {
                      label: React.ReactNode
                      value: string
                    }[]
                  }
                  value={field.value ?? []}
                  placeholder={customPlaceholder}
                  onChange={(value) => {
                    field.onChange(value)
                    onChange?.(value)
                  }}
                />
              ) : (
                <Select
                  {...props}
                  value={currentValue}
                  options={
                    transformedOptions as {
                      label: React.ReactNode
                      value: string
                    }[]
                  }
                  placeholder={customPlaceholder}
                  className={`${
                    layout === 'box' ? `w-full md:w-40 md:h-8` : ''
                  } ${inputClassName ?? ''}`}
                  setValue={(value) => {
                    field.onChange(value ?? null)
                    onChange?.(value)
                  }}
                />
              )}
            </FieldWrapper>
          )
        }}
      />
    </div>
  )
}

export default SelectField
